import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

interface VideoItem {
  id: string;
  url: string;
  title: string;
  thumbnail: string;
  duration?: string;
}

@Component({
  selector: 'app-video-carousel',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './video-caraousel.component.html',
  styleUrls: ['./video-caraousel.component.scss'],
})
export class VideoCaraouselComponent implements OnInit, OnDestroy {
  @ViewChild('carousel') carousel!: ElementRef;

  currentIndex = 0;
  slidesToShow = 4;
  slideWidth = 25; // 100 / slidesToShow
  selectedVideo: VideoItem | null = null;

  private resizeObserver!: ResizeObserver;
  private autoSlideInterval?: number;

  constructor(private sanitizer: DomSanitizer) {}

  // Sample video data - replace with your actual YouTube URLs
  videos: VideoItem[] = [
    {
      id: 'UwgwPjNRD1Q',
      url: 'https://www.youtube.com/shorts/UwgwPjNRD1Q',
      title: 'Rick Astley - Never Gonna Give You Up (Official Music Video)',
      thumbnail: 'https://img.youtube.com/vi/dQw4w9WgXcQ/hqdefault.jpg',
      duration: '0:51',
    },
    {
      id: 'L_jWHffIx5E',
      url: 'https://www.youtube.com/watch?v=L_jWHffIx5E',
      title: 'Smash Mouth - All Star (Official Music Video)',
      thumbnail: 'https://img.youtube.com/vi/L_jWHffIx5E/hqdefault.jpg',
      duration: '3:20',
    },
    {
      id: '9bZkp7q19f0',
      url: 'https://www.youtube.com/watch?v=9bZkp7q19f0',
      title: 'PSY - GANGNAM STYLE (강남스타일) M/V',
      thumbnail: 'https://img.youtube.com/vi/9bZkp7q19f0/hqdefault.jpg',
      duration: '4:13',
    },
    {
      id: 'kJQP7kiw5Fk',
      url: 'https://www.youtube.com/watch?v=kJQP7kiw5Fk',
      title: 'Luis Fonsi - Despacito ft. Daddy Yankee',
      thumbnail: 'https://img.youtube.com/vi/kJQP7kiw5Fk/hqdefault.jpg',
      duration: '4:42',
    },
    {
      id: 'fJ9rUzIMcZQ',
      url: 'https://www.youtube.com/watch?v=fJ9rUzIMcZQ',
      title: 'Queen – Bohemian Rhapsody (Official Music Video)',
      thumbnail: 'https://img.youtube.com/vi/fJ9rUzIMcZQ/hqdefault.jpg',
      duration: '5:55',
    },
    {
      id: '60ItHLz5WEA',
      url: 'https://www.youtube.com/watch?v=60ItHLz5WEA',
      title: 'Alan Walker - Faded',
      thumbnail: 'https://img.youtube.com/vi/60ItHLz5WEA/hqdefault.jpg',
      duration: '3:33',
    },
  ];

  ngOnInit() {
    this.updateResponsiveSettings();
    this.setupResizeObserver();
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    // Restore body scroll on component destroy
    document.body.style.overflow = 'auto';
  }

  private setupResizeObserver() {
    this.resizeObserver = new ResizeObserver(() => {
      this.updateResponsiveSettings();
    });

    if (this.carousel) {
      this.resizeObserver.observe(this.carousel.nativeElement);
    }
  }

  private updateResponsiveSettings() {
    const width = window.innerWidth;

    if (width < 640) {
      // Mobile
      this.slidesToShow = 1;
    } else if (width < 768) {
      // Small tablet
      this.slidesToShow = 2;
    } else if (width < 1024) {
      // Tablet
      this.slidesToShow = 3;
    } else {
      // Desktop
      this.slidesToShow = 4;
    }

    this.slideWidth = 100 / this.slidesToShow;

    // Adjust current index if needed
    const maxIndex = Math.max(0, this.videos.length - this.slidesToShow);
    if (this.currentIndex > maxIndex) {
      this.currentIndex = maxIndex;
    }
  }

  nextSlide() {
    const maxIndex = Math.max(0, this.videos.length - this.slidesToShow);
    if (this.currentIndex < maxIndex) {
      this.currentIndex++;
    } else {
      this.currentIndex = 0; // Loop back to start
    }
  }

  prevSlide() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
    } else {
      const maxIndex = Math.max(0, this.videos.length - this.slidesToShow);
      this.currentIndex = maxIndex; // Loop to end
    }
  }

  goToSlide(index: number) {
    this.currentIndex = index * this.slidesToShow;
  }

  canGoPrev(): boolean {
    return this.videos.length > this.slidesToShow;
  }

  canGoNext(): boolean {
    return this.videos.length > this.slidesToShow;
  }

  getIndicators(): number[] {
    const indicatorCount = Math.ceil(this.videos.length / this.slidesToShow);
    return Array(indicatorCount).fill(0);
  }

  openVideo(video: VideoItem) {
    this.selectedVideo = video;
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
  }

  closeVideo() {
    this.selectedVideo = null;
    // Restore body scroll
    document.body.style.overflow = 'auto';
    // Don't restart auto-slide immediately to prevent interference

  }

  getEmbedUrl(url: string): SafeResourceUrl {
    const videoId = this.extractVideoId(url);
    const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
  }

  private extractVideoId(url: string): string {
    const match = url.match(
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/
    );
    return match ? match[1] : '';
  }




  get getIndex() {
    return Math.floor(this.currentIndex / this.slidesToShow);
  }
}
